import { DocViewerTab } from '@enums/doc-viewer-tab.enum';
import { TabPreferencesService } from '@services/tab-preferences.service';
import { TabPreference } from '@models/review/tab-preference';
import { filter, forkJoin, map, Observable, of, ReplaySubject, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { UserConfigsService } from '@services/user-configs.service';
import { Role } from '@models/role.model';
import { MainTabIndices } from '@enums/main-tab-indices.enum';
import { Injectable } from '@angular/core';
import { DocViewerParamReader } from './doc-viewer-param-reader';
import { AnalysisManager } from '@managers/analysis.manager';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { HelpService } from '@services/help.service';

@Injectable({
	providedIn: 'root'
})
export class TabStates {
	public enabled = {};
	public loaded = {};
	public mainTabSelectedIndex = 4;
	public rightTabSelectedIndex = 0;
	public currentSelectedTabIndex$: Observable<MainTabIndices>;
	public rightTabPref: TabPreference;
	public mainTabPref: TabPreference;
	public isTabStatesReady: boolean = false;
	private currentSelectedTabIndexSource: ReplaySubject<MainTabIndices> = new ReplaySubject<MainTabIndices>(1);

	private readonly mainTabId = '4bd82229-99c1-4c5b-93c8-fd4d588742a1';
	private readonly rightTabId = '4bd82229-99c1-4c5b-93c8-fd4d588742a2';
	private visibleSource = new ReplaySubject<Array<Record<string, boolean>>>();
	private diffDocId: string;
	private ignoreUiChange = false;

	constructor(
		private tabsPreferenceService: TabPreferencesService,
		private userConfigsService: UserConfigsService,
		private paramReader: DocViewerParamReader,
		private analysisManager: AnalysisManager,
		private docViewerStore: DocViewerStore,
		private helpService: HelpService
	) {
		this.currentSelectedTabIndex$ = this.currentSelectedTabIndexSource.asObservable();

		this.docViewerStore.simpleState$.pipe(
			tap((currentState) => {
				this.diffDocId = currentState.compareDocumentId;
				if (this.mainTabPref) {
					this.updateAndValidateTabStates(currentState);
				}
			})
		).subscribe();

		this.docViewerStore.currentDocument$.pipe(
			switchMap(() => this.saveRightTabPreference$())
		).subscribe();

		forkJoin([
			this.docViewerStore.currentDocument$.pipe(take(1)),
			this.loadTabPreferences$()
		]).pipe(
			map(() => {
				if (this.paramReader.fromSearch && this.mainTabSelectedIndex !== MainTabIndices.Hits) {
					this.mainTabSelectedIndex = MainTabIndices.Hits;
					this.saveMainTabPreference();
				}
				if (this.paramReader.fromAutoRedaction && this.mainTabSelectedIndex !== MainTabIndices.Redacted) {
					this.mainTabSelectedIndex = MainTabIndices.Redacted;
				}
				this.loaded[this.mainTabSelectedIndex] = true;
				return MainTabIndices[MainTabIndices[this.mainTabSelectedIndex]];
			}),
			withLatestFrom(this.docViewerStore.simpleState$),
			tap(([selectedIndex, simpleState]) => {
				this.updateAndValidateTabStates(simpleState);
				this.currentSelectedTabIndexSource.next(selectedIndex);
				this.isTabStatesReady = true;
			})
		).subscribe(() => {
			if (this.rightTabSelectedIndex === 7)
				this.helpService.updateHelpLink('document-viewer/smart-tab');
			else
				this.helpService.updateHelpLink('document-viewer');
		});

	}

	private _visible = new Array<Record<string, boolean>>();

	public get visible(): Observable<Array<Record<string, boolean>>> {
		return this.visibleSource.asObservable();
	}

	public onMainTabChange(): void {
		if (this.ignoreUiChange) {
			this.ignoreUiChange = false;
			return;
		}

		this.loaded[this.mainTabSelectedIndex] = true;
		if (!this.mainTabPref || this.mainTabPref.tabIndex !== this.mainTabSelectedIndex) {
			this.saveMainTabPreference();
		}

		if (this.isRightTabInvalid(this.mainTabSelectedIndex, this.rightTabSelectedIndex)) {
			this.rightTabSelectedIndex = 0;
		}

	}

	public saveMainTabPreference(): void {
		const newPref = { tabId: this.mainTabId, tabIndex: this.mainTabSelectedIndex };
		this.mainTabPref = newPref;
		this.tabsPreferenceService.savePreference(newPref).subscribe();
	}

	public saveRightTabPreference$(): Observable<void> {
		if (!this.rightTabPref || this.rightTabPref.tabIndex !== this.rightTabSelectedIndex && (this.rightTabSelectedIndex < 5 || this.rightTabSelectedIndex == 7)) {
			return this.tabsPreferenceService.savePreference({
				tabId: this.rightTabId,
				tabIndex: this.rightTabSelectedIndex
			}).pipe(
				map(() => {
					if (!this.rightTabPref) {
						this.rightTabPref = {} as TabPreference;
					}
					this.rightTabPref.tabIndex = this.rightTabSelectedIndex;
					this.rightTabPref.tabId = this.rightTabId;
				})
			);
		}
		return of(null);
	}

	public changeMainTab(tab: DocViewerTab): void {
		switch (tab) {
			case DocViewerTab.Pdf:
				this.mainTabSelectedIndex = MainTabIndices.PDF;
				break;
			case DocViewerTab.Page:
				this.mainTabSelectedIndex = MainTabIndices.Page;
				break;
			case DocViewerTab.Text:
				this.mainTabSelectedIndex = MainTabIndices.Text;
				break;
			default:
				console.warn('Unknown tab change: ' + tab);
				break;
		}
	}

	public hideCompareState(): void {
		this._visible[DocViewerTab.Compare] = this.enabled[MainTabIndices.Compare] = false;
		this.visibleSource.next(this._visible);
	}

	public showCompareState(setActive: boolean): void {
		this._visible[DocViewerTab.Compare] = this.enabled[MainTabIndices.Compare] = true;
		this.visibleSource.next(this._visible);
		if (setActive) {
			this.mainTabSelectedIndex = MainTabIndices.Compare;
			this.saveMainTabPreference();
		}
	}

	private updateAndValidateTabStates(currentState): void {
		this.visibleSource.next(this._visible);
		for (let i = 0; i < 11; i++) {
			this.loaded[i] = false;
		}

		this.enabled[MainTabIndices.Hits] = this.paramReader.fromSearch;
		this.enabled[MainTabIndices.Original] = currentState.hasOriginalRendering;
		this.enabled[MainTabIndices.Page] = currentState.hasPdfRendering;
		this.enabled[MainTabIndices.PDF] = currentState.hasPdfRendering;

		this._visible[DocViewerTab.Hits] = true;
		this._visible[DocViewerTab.Original] = true;
		this._visible[DocViewerTab.Page] = true;
		this._visible[DocViewerTab.Pdf] = true;

		this._visible[DocViewerTab.Html] = this.enabled[MainTabIndices.HTML] = true;
		this._visible[DocViewerTab.Text] = this.enabled[MainTabIndices.Text] = true;
		this._visible[DocViewerTab.RedactedPdf] = this.enabled[MainTabIndices.Redacted] = true;
		this._visible[DocViewerTab.AnnotatedPdf] = this.enabled[MainTabIndices.Annotated] = true;

		this._visible[DocViewerTab.Production] = this.enabled[MainTabIndices.Produced] = currentState.hasProduction;
		this._visible[DocViewerTab.Translated] = this.enabled[MainTabIndices.Translated] = currentState.hasTranslation;
		this._visible[DocViewerTab.Compare] = this.enabled[MainTabIndices.Compare] = this.diffDocId != null;

		this.visibleSource.next(this._visible);

		this.ignoreUiChange = false;

		// Make sure the currently selected tab is still valid for the current document
		// If not, fallback to PDF|HTML
		if (this.mainTabSelectedIndex !== MainTabIndices.Hits && this.mainTabPref && this.isThisMainTabValid(this.mainTabPref.tabIndex)) {
			this.mainTabSelectedIndex = this.mainTabPref.tabIndex;
		} else if (this.mainTabSelectedIndex !== MainTabIndices.Hits && !this.isThisMainTabValid(this.mainTabSelectedIndex)) {
			this.ignoreUiChange = true;
			if (this.enabled[MainTabIndices.PDF])
				this.mainTabSelectedIndex = MainTabIndices.PDF;
			else
				this.mainTabSelectedIndex = MainTabIndices.HTML;
		}
	}

	private loadTabPreferences$(): Observable<void> {
		return this.docViewerStore.simpleState$.pipe(
			filter((x) => !!x.tabPreferences),
			take(1),
			map((x) => x.tabPreferences)
		).pipe(
			map((prefs) => {
				this.mainTabPref = prefs.find(p => p.tabId === this.mainTabId);
				this.rightTabPref = prefs.find(p => p.tabId === this.rightTabId);

				if (!this.rightTabPref) {
					this.rightTabPref = {} as TabPreference;
				}

				this.loaded[this.mainTabSelectedIndex] = true;

				if (this.userConfigsService.inRoles(Role.LimitedFeatureSet)) {
					this.rightTabSelectedIndex = 0; // A limited user only has access to the DISC tab
				}
				else if (this.mainTabSelectedIndex != MainTabIndices.Redacted && this.mainTabSelectedIndex != MainTabIndices.Annotated) {
					this.rightTabSelectedIndex = this.rightTabPref.tabIndex;
				}
			})
		);
	}

	private isRightTabInvalid(mainTabIndex: number, rightTabIndex: number): boolean {
		const redactionTab = 5;
		const annotationTab = 6;

		return (rightTabIndex === redactionTab && mainTabIndex !== MainTabIndices.Redacted) ||
			(rightTabIndex === annotationTab && mainTabIndex !== MainTabIndices.Annotated);
	}

	private isThisMainTabValid(tabIndex: number): boolean {
		return tabIndex < Object.keys(this.enabled).length && this.enabled[tabIndex];
	}
}
